import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  onSnapshot,
  updateDoc 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './AuthContext';

const FinanceContext = createContext();

export const useFinance = () => {
  const context = useContext(FinanceContext);
  if (!context) {
    throw new Error('useFinance must be used within a FinanceProvider');
  }
  return context;
};

export const FinanceProvider = ({ children }) => {
  const { user } = useAuth();
  const [financeData, setFinanceData] = useState({
    salary: 0,
    currentMoney: 0,
    rent: 0,
    rentPaid: false,
    payments: []
  });
  const [loading, setLoading] = useState(true);

  // Cargar datos desde Firebase
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const userDocRef = doc(db, 'users', user.uid);

    const unsubscribe = onSnapshot(userDocRef, async (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        const newFinanceData = {
          salary: data.salary || 0,
          currentMoney: data.currentMoney || 0,
          rent: data.rent || 0,
          rentPaid: data.rentPaid || false,
          payments: data.payments || []
        };
        setFinanceData(newFinanceData);
      } else {
        // Crear documento inicial si no existe
        const initialData = {
          salary: 0,
          currentMoney: 0,
          rent: 0,
          rentPaid: false,
          payments: []
        };
        await setDoc(userDocRef, initialData);
      }
      setLoading(false);
    }, (error) => {
      console.error("Error loading data:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  // Guardar datos en Firebase
  const saveToFirebase = async (data) => {
    if (!user) return false;
    
    try {
      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, data, { merge: true });
      return true;
    } catch (error) {
      console.error("Error saving to Firebase:", error);
      return false;
    }
  };

  // Actualizar información básica
  const updateBasicInfo = async (salary, currentMoney, rent) => {
    const newData = { ...financeData, salary, currentMoney, rent };
    setFinanceData(newData);
    await saveToFirebase(newData);
  };

  // Cambiar estado del alquiler
  const toggleRentPaid = async () => {
    const newData = { ...financeData, rentPaid: !financeData.rentPaid };
    setFinanceData(newData);
    await saveToFirebase(newData);
  };

  // Agregar nuevo pago
  const addPayment = async (payment) => {
    const newPayment = {
      id: Date.now().toString(),
      ...payment,
      paid: false
    };
    
    // Si no se especifica el modo de fechas, usar fechas exactas por defecto
    if (newPayment.useExactDates === undefined) {
      newPayment.useExactDates = true;
    }
    
    // Si usa fechas exactas pero no tiene startDate, usar fecha actual
    if (newPayment.useExactDates && !newPayment.startDate) {
      newPayment.startDate = new Date().toISOString().split('T')[0];
    }
    
    // Si usa sistema antiguo pero no tiene startMonth, usar mes actual
    if (!newPayment.useExactDates && !newPayment.startMonth) {
      newPayment.startMonth = new Date().getMonth() + 1;
    }
    
    const newData = { 
      ...financeData, 
      payments: [...financeData.payments, newPayment] 
    };
    setFinanceData(newData);
    await saveToFirebase(newData);
  };

  // Actualizar pago
  const updatePayment = async (id, updatedPayment) => {
    const newPayments = financeData.payments.map(p => 
      p.id === id ? { ...p, ...updatedPayment } : p
    );
    const newData = { ...financeData, payments: newPayments };
    setFinanceData(newData);
    await saveToFirebase(newData);
  };

  // Eliminar pago
  const deletePayment = async (id) => {
    const newPayments = financeData.payments.filter(p => p.id !== id);
    const newData = { ...financeData, payments: newPayments };
    setFinanceData(newData);
    await saveToFirebase(newData);
  };

  // Cambiar estado de pago
  const togglePayment = async (id) => {
    const newPayments = financeData.payments.map(p => 
      p.id === id ? { ...p, paid: !p.paid } : p
    );
    const newData = { ...financeData, payments: newPayments };
    setFinanceData(newData);
    await saveToFirebase(newData);
  };

  // Reiniciar todos los pagos
  const resetAllPayments = async () => {
    const newPayments = financeData.payments.map(p => ({ ...p, paid: false }));
    const newData = { 
      ...financeData, 
      payments: newPayments,
      rentPaid: false 
    };
    setFinanceData(newData);
    await saveToFirebase(newData);
  };

  // Limpiar todos los datos
  const clearAllData = async () => {
    const newData = {
      salary: 0,
      currentMoney: 0,
      rent: 0,
      rentPaid: false,
      payments: []
    };
    setFinanceData(newData);
    await saveToFirebase(newData);
  };

  // Exportar datos
  const exportData = () => {
    const dataStr = JSON.stringify(financeData, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `finanzas_backup_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Importar datos desde JSON
  const importData = async (jsonData) => {
    try {
      // Validar que el JSON tenga la estructura correcta
      if (!jsonData.salary && !jsonData.payments) {
        throw new Error('Formato de archivo inválido');
      }

      // Procesar los datos importados
      const importedData = {
        salary: jsonData.salary || 0,
        currentMoney: jsonData.currentMoney || 0,
        rent: jsonData.rent || 0,
        rentPaid: jsonData.rentPaid || false,
        payments: jsonData.payments || []
      };

      // Asignar IDs únicos a los pagos si no los tienen y normalizar estructura
      importedData.payments = importedData.payments.map(payment => {
        const normalizedPayment = {
          ...payment,
          id: payment.id || Date.now().toString()
        };
        
        // Si no tiene useExactDates definido, determinar basado en los campos disponibles
        if (normalizedPayment.useExactDates === undefined) {
          normalizedPayment.useExactDates = !!(payment.startDate && payment.endDate);
        }
        
        // Asegurar que tenga al menos un sistema de fechas
        if (!normalizedPayment.useExactDates && !normalizedPayment.startMonth) {
          normalizedPayment.startMonth = new Date().getMonth() + 1;
        }
        
        return normalizedPayment;
      });

      setFinanceData(importedData);
      await saveToFirebase(importedData);
      return true;
    } catch (error) {
      console.error("Error importing data:", error);
      return false;
    }
  };

  const value = {
    financeData,
    loading,
    updateBasicInfo,
    toggleRentPaid,
    addPayment,
    updatePayment,
    deletePayment,
    togglePayment,
    resetAllPayments,
    clearAllData,
    exportData,
    importData
  };

  return (
    <FinanceContext.Provider value={value}>
      {children}
    </FinanceContext.Provider>
  );
};